import { useState } from "react";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Car, Bike, FileText, Download, Loader2, Ship, Truck, RefreshCw, Sparkles, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Certificates() {
  const [selectedType, setSelectedType] = useState(null);
  const [loading, setLoading] = useState(false);
  const [useRandomBuyer, setUseRandomBuyer] = useState(false);
  const [useRandomPrice, setUseRandomPrice] = useState(false);
  const [useRandomDate, setUseRandomDate] = useState(false);
  const [loadingRandomBuyer, setLoadingRandomBuyer] = useState(false);
  const [formData, setFormData] = useState({
    sellerFullName: "",
    sellerPassport: "",
    sellerCity: "",
    buyerFullName: "",
    buyerPassport: "",
    buyerCity: "",
    date: new Date().toISOString().split('T')[0],
    amount: ""
  });
  
  // State for Registration certificate
  const [registrationData, setRegistrationData] = useState({
    documentNumber: "100140995",
    documentDate: "04.02.2026",
    fullName: "ПОТЕХИН ЮРИЙ ВЛАДИМИРОВИЧ",
    inn: "391802588200",
    passportSeries: "2717",
    passportNumber: "685064",
    registrationDate: "13.02.2024"
  });

  const documentTypes = [
    {
      id: "car",
      title: "Автотранспорт",
      description: "Договор купли-продажи автомобиля",
      icon: Car,
      color: "from-blue-500 to-blue-600"
    },
    {
      id: "motorcycle",
      title: "Мотоцикл",
      description: "Договор купли-продажи мотоцикла",
      icon: Bike,
      color: "from-orange-500 to-orange-600"
    },
    {
      id: "boat",
      title: "Моторная лодка",
      description: "Договор купли-продажи лодки",
      icon: Ship,
      color: "from-cyan-500 to-cyan-600"
    },
    {
      id: "atv",
      title: "Квадроцикл",
      description: "Договор купли-продажи квадроцикла",
      icon: Truck,
      color: "from-green-500 to-green-600"
    },
    {
      id: "registration",
      title: "Постановка на учет",
      description: "Справка о постановке на учет в налоговой",
      icon: ClipboardCheck,
      color: "from-purple-500 to-purple-600"
    }
  ];

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" };
  };

  const fetchRandomBuyer = async () => {
    setLoadingRandomBuyer(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/personal-data/random`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const buyer = await response.json();
        setFormData(prev => ({
          ...prev,
          buyerFullName: buyer.full_name || "",
          buyerPassport: buyer.passport || ""
          // Город НЕ загружается - вводится вручную
        }));
        toast.success("Случайный покупатель загружен");
      } else {
        toast.error("Не удалось загрузить данные покупателя");
      }
    } catch (error) {
      console.error("Error fetching random buyer:", error);
      toast.error("Ошибка загрузки данных");
    } finally {
      setLoadingRandomBuyer(false);
    }
  };

  const generateRandomPrice = () => {
    // Random price from 300,000 to 860,000
    const price = Math.floor(Math.random() * (860000 - 300000 + 1)) + 300000;
    setFormData(prev => ({ ...prev, amount: price.toString() }));
    toast.success(`Случайная цена: ${price.toLocaleString('ru-RU')} ₽`);
  };

  const generateRandomDate = () => {
    // Random date from December 1-30, 2026
    const day = Math.floor(Math.random() * 30) + 1;
    const date = `2026-12-${day.toString().padStart(2, '0')}`;
    setFormData(prev => ({ ...prev, date }));
    toast.success(`Случайная дата: ${day} декабря 2026 г.`);
  };

  const handleTypeSelect = (typeId) => {
    setSelectedType(typeId);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleGenerateRegistration = async () => {
    if (!registrationData.fullName || !registrationData.inn) {
      toast.error("Заполните ФИО и ИНН");
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/certificates/generate-registration`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(registrationData)
      });

      const data = await response.json();
      
      if (data.success) {
        const link = document.createElement('a');
        link.href = `data:application/pdf;base64,${data.pdf}`;
        link.download = data.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success("Справка успешно создана");
      } else {
        toast.error(data.error || "Ошибка генерации справки");
      }
    } catch (error) {
      console.error("Error generating registration:", error);
      toast.error("Ошибка при создании справки");
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!selectedType) {
      toast.error("Выберите тип документа");
      return;
    }

    if (!formData.sellerFullName) {
      toast.error("Введите ФИО продавца");
      return;
    }

    // If not using random buyer and buyer name is empty
    if (!useRandomBuyer && !formData.buyerFullName) {
      toast.error("Введите ФИО покупателя или включите случайного покупателя");
      return;
    }

    // If not using random price and amount is empty
    if (!useRandomPrice && !formData.amount) {
      toast.error("Введите сумму сделки или включите случайную цену");
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      
      // Generate values if checkboxes are selected
      let finalFormData = { ...formData };
      
      if (useRandomBuyer && !formData.buyerFullName) {
        const response = await fetch(`${API_URL}/api/admin/personal-data/random`, {
          headers: getAuthHeaders()
        });
        if (response.ok) {
          const buyer = await response.json();
          finalFormData.buyerFullName = buyer.full_name || "Иванов Иван Иванович";
          finalFormData.buyerPassport = buyer.passport || "";
          // Город НЕ загружается - вводится вручную
        }
      }
      
      if (useRandomPrice && !formData.amount) {
        finalFormData.amount = (Math.floor(Math.random() * (860000 - 300000 + 1)) + 300000).toString();
      }
      
      if (useRandomDate) {
        const day = Math.floor(Math.random() * 30) + 1;
        finalFormData.date = `2026-12-${day.toString().padStart(2, '0')}`;
      }

      const response = await fetch(`${API_URL}/api/certificates/generate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          type: selectedType,
          ...finalFormData
        })
      });

      const data = await response.json();
      
      if (data.success) {
        // Download PDF
        const link = document.createElement('a');
        link.href = `data:application/pdf;base64,${data.pdf}`;
        link.download = data.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success("Документ успешно создан");
      } else {
        toast.error(data.error || "Ошибка генерации документа");
      }
    } catch (error) {
      console.error("Error generating certificate:", error);
      toast.error("Ошибка при создании документа");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="certificates-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Справки</h1>
          <p className="text-slate-500 mt-1">Генерация документов купли-продажи</p>
        </div>
      </div>

      {/* Document Type Selection - Compact Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {documentTypes.map((type) => (
          <Card
            key={type.id}
            className={`p-3 cursor-pointer transition-all duration-200 hover:shadow-md ${
              selectedType === type.id
                ? "ring-2 ring-blue-500 shadow-md bg-blue-50/50"
                : "hover:bg-slate-50"
            }`}
            onClick={() => handleTypeSelect(type.id)}
            data-testid={`doc-type-${type.id}`}
          >
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg bg-gradient-to-br ${type.color} shadow-sm`}>
                <type.icon className="w-4 h-4 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-sm text-slate-800 truncate">{type.title}</h3>
                <p className="text-xs text-slate-500 truncate">{type.description}</p>
              </div>
              {selectedType === type.id && (
                <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              )}
            </div>
          </Card>
        ))}
      </div>

      {/* Form */}
      {selectedType && selectedType !== "registration" && (
        <Card className="p-6" data-testid="certificate-form">
          <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-500" />
            Настройки документа
          </h2>

          {/* Random Options - Available for ALL types */}
          <div className="mb-6 p-4 bg-purple-50 rounded-lg border border-purple-200">
            <h3 className="font-medium text-purple-800 mb-3 flex items-center gap-2 text-sm">
              <Sparkles className="w-4 h-4" />
              Автоматическая генерация данных
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="randomBuyer" 
                  checked={useRandomBuyer}
                  onCheckedChange={setUseRandomBuyer}
                />
                <label htmlFor="randomBuyer" className="text-sm text-purple-700 cursor-pointer">
                  Случайный покупатель
                </label>
                {useRandomBuyer && (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={fetchRandomBuyer}
                    disabled={loadingRandomBuyer}
                    className="h-6 px-2 text-purple-600"
                  >
                    {loadingRandomBuyer ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                  </Button>
                )}
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="randomPrice" 
                  checked={useRandomPrice}
                  onCheckedChange={setUseRandomPrice}
                />
                <label htmlFor="randomPrice" className="text-sm text-purple-700 cursor-pointer">
                  Случайная цена (300-860 тыс.)
                </label>
                {useRandomPrice && (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={generateRandomPrice}
                    className="h-6 px-2 text-purple-600"
                  >
                    <RefreshCw className="w-3 h-3" />
                  </Button>
                )}
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="randomDate" 
                  checked={useRandomDate}
                  onCheckedChange={setUseRandomDate}
                />
                <label htmlFor="randomDate" className="text-sm text-purple-700 cursor-pointer">
                  Случайная дата (дек. 2026)
                </label>
                {useRandomDate && (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={generateRandomDate}
                    className="h-6 px-2 text-purple-600"
                  >
                    <RefreshCw className="w-3 h-3" />
                  </Button>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Seller Section */}
            <div className="space-y-3">
              <h3 className="font-medium text-slate-700 border-b pb-2">Продавец</h3>
              
              <div className="space-y-1">
                <Label htmlFor="sellerFullName" className="text-sm">ФИО продавца *</Label>
                <Input
                  id="sellerFullName"
                  name="sellerFullName"
                  placeholder="Иванов Иван Иванович"
                  value={formData.sellerFullName}
                  onChange={handleInputChange}
                  data-testid="seller-fullname-input"
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="sellerPassport" className="text-sm">Паспортные данные</Label>
                <Input
                  id="sellerPassport"
                  name="sellerPassport"
                  placeholder="4512 123456"
                  value={formData.sellerPassport}
                  onChange={handleInputChange}
                  data-testid="seller-passport-input"
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="sellerCity" className="text-sm">Город</Label>
                <Input
                  id="sellerCity"
                  name="sellerCity"
                  placeholder="Москва"
                  value={formData.sellerCity}
                  onChange={handleInputChange}
                  data-testid="seller-city-input"
                />
              </div>
            </div>

            {/* Buyer Section */}
            <div className="space-y-3">
              <h3 className="font-medium text-slate-700 border-b pb-2">
                Покупатель
                {useRandomBuyer && (
                  <span className="ml-2 text-xs text-purple-600 font-normal">(из базы)</span>
                )}
              </h3>
              
              <div className="space-y-1">
                <Label htmlFor="buyerFullName" className="text-sm">
                  ФИО покупателя {!useRandomBuyer && "*"}
                </Label>
                <Input
                  id="buyerFullName"
                  name="buyerFullName"
                  placeholder={useRandomBuyer ? "Загружается из базы..." : "Петров Пётр Петрович"}
                  value={formData.buyerFullName}
                  onChange={handleInputChange}
                  className={useRandomBuyer && !formData.buyerFullName ? "bg-purple-50" : ""}
                  data-testid="buyer-fullname-input"
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="buyerPassport" className="text-sm">
                  Паспортные данные
                  {useRandomBuyer && (
                    <span className="ml-1 text-xs text-purple-600">(из базы)</span>
                  )}
                </Label>
                <Input
                  id="buyerPassport"
                  name="buyerPassport"
                  placeholder={useRandomBuyer ? "Загружается из базы..." : "4513 654321"}
                  value={formData.buyerPassport}
                  onChange={handleInputChange}
                  className={useRandomBuyer && !formData.buyerPassport ? "bg-purple-50" : ""}
                  data-testid="buyer-passport-input"
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="buyerCity" className="text-sm">Город</Label>
                <Input
                  id="buyerCity"
                  name="buyerCity"
                  placeholder="Санкт-Петербург"
                  value={formData.buyerCity}
                  onChange={handleInputChange}
                  data-testid="buyer-city-input"
                />
              </div>
            </div>
          </div>

          {/* Common Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6 pt-4 border-t">
            <div className="space-y-1">
              <Label htmlFor="date" className="text-sm">
                Дата сделки
                {useRandomDate && (
                  <span className="ml-2 text-xs text-purple-600">(01-30 дек. 2026)</span>
                )}
              </Label>
              <Input
                id="date"
                name="date"
                type="date"
                value={formData.date}
                onChange={handleInputChange}
                className={useRandomDate ? "bg-purple-50" : ""}
                data-testid="date-input"
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="amount" className="text-sm">
                Сумма сделки (₽) {!useRandomPrice && "*"}
                {useRandomPrice && (
                  <span className="ml-2 text-xs text-purple-600">(300-860 тыс.)</span>
                )}
              </Label>
              <Input
                id="amount"
                name="amount"
                type="number"
                placeholder={useRandomPrice ? "Генерируется автоматически" : "500000"}
                value={formData.amount}
                onChange={handleInputChange}
                className={useRandomPrice && !formData.amount ? "bg-purple-50" : ""}
                data-testid="amount-input"
              />
            </div>
          </div>

          {/* Info Box */}
          <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
            <p className="text-sm text-blue-700">
              <strong>Автогенерация:</strong> марка, модель, госномер, VIN, год выпуска, подписи сторон.
              PDF с эффектом сканирования.
            </p>
          </div>

          {/* Generate Button */}
          <div className="mt-4 flex justify-end">
            <Button
              onClick={handleGenerate}
              disabled={loading}
              className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-8"
              data-testid="generate-certificate-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Генерация...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Создать документ
                </>
              )}
            </Button>
          </div>
        </Card>
      )}

      {/* Registration Certificate Form */}
      {selectedType === "registration" && (
        <Card className="p-6" data-testid="registration-form">
          <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <ClipboardCheck className="w-5 h-5 text-purple-500" />
            Справка о постановке на учет
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Document Info */}
            <div className="space-y-4">
              <h3 className="font-medium text-slate-700 border-b pb-2">Данные документа</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label htmlFor="docNumber" className="text-sm">№ документа</Label>
                  <Input
                    id="docNumber"
                    value={registrationData.documentNumber}
                    onChange={(e) => setRegistrationData(prev => ({ ...prev, documentNumber: e.target.value }))}
                    placeholder="100140995"
                    data-testid="reg-doc-number"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="docDate" className="text-sm">Дата документа</Label>
                  <Input
                    id="docDate"
                    value={registrationData.documentDate}
                    onChange={(e) => setRegistrationData(prev => ({ ...prev, documentDate: e.target.value }))}
                    placeholder="04.02.2026"
                    data-testid="reg-doc-date"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <Label htmlFor="regDate" className="text-sm">Дата постановки на учет</Label>
                <Input
                  id="regDate"
                  value={registrationData.registrationDate}
                  onChange={(e) => setRegistrationData(prev => ({ ...prev, registrationDate: e.target.value }))}
                  placeholder="13.02.2024"
                  data-testid="reg-registration-date"
                />
              </div>
            </div>

            {/* Personal Info */}
            <div className="space-y-4">
              <h3 className="font-medium text-slate-700 border-b pb-2">Данные физического лица</h3>
              
              <div className="space-y-1">
                <Label htmlFor="regFullName" className="text-sm">ФИО (заглавными буквами)</Label>
                <Input
                  id="regFullName"
                  value={registrationData.fullName}
                  onChange={(e) => setRegistrationData(prev => ({ ...prev, fullName: e.target.value.toUpperCase() }))}
                  placeholder="ПОТЕХИН ЮРИЙ ВЛАДИМИРОВИЧ"
                  className="uppercase"
                  data-testid="reg-full-name"
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="regInn" className="text-sm">ИНН</Label>
                <Input
                  id="regInn"
                  value={registrationData.inn}
                  onChange={(e) => setRegistrationData(prev => ({ ...prev, inn: e.target.value }))}
                  placeholder="391802588200"
                  maxLength={12}
                  data-testid="reg-inn"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label htmlFor="regPassportSeries" className="text-sm">Серия паспорта</Label>
                  <Input
                    id="regPassportSeries"
                    value={registrationData.passportSeries}
                    onChange={(e) => setRegistrationData(prev => ({ ...prev, passportSeries: e.target.value }))}
                    placeholder="2717"
                    maxLength={4}
                    data-testid="reg-passport-series"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="regPassportNumber" className="text-sm">Номер паспорта</Label>
                  <Input
                    id="regPassportNumber"
                    value={registrationData.passportNumber}
                    onChange={(e) => setRegistrationData(prev => ({ ...prev, passportNumber: e.target.value }))}
                    placeholder="685064"
                    maxLength={6}
                    data-testid="reg-passport-number"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Info Box */}
          <div className="mt-4 p-3 bg-purple-50 rounded-lg border border-purple-100">
            <p className="text-sm text-purple-700">
              <strong>Справка из ФНС:</strong> официальный бланк с гербом РФ, номером документа и подписью начальника.
            </p>
          </div>

          {/* Generate Button */}
          <div className="mt-4 flex justify-end">
            <Button
              onClick={handleGenerateRegistration}
              disabled={loading}
              className="bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white px-8"
              data-testid="generate-registration-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Генерация...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Создать справку
                </>
              )}
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
